"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.xconnect = xconnect;
exports.xhangup = xhangup;
exports.xsend = xsend;
exports.xsendRaw = xsendRaw;
exports.xrecv = xrecv;
exports.xsendr = xsendr;
exports.xreset = xreset;
exports.xtype = xtype;
exports.xvers = xvers;
exports.xsetpar = xsetpar;
exports.xsetparBytes = xsetparBytes;
exports.xawlen = xawlen;
exports.xawlenBytes = xawlenBytes;
exports.xsendf = xsendf;
exports.xrequf = xrequf;
exports.xstopf = xstopf;
exports.xkeyb = xkeyb;
exports.xstate = xstate;
exports.xboot = xboot;
exports.xreps = xreps;
exports.xgetport = xgetport;
exports.xsetport = xsetport;
exports.xbatt = xbatt;
exports.xignit = xignit;
exports.xloopt = xloopt;
exports.xprog = xprog;
exports.xraw = xraw;
exports.xsireset = xsireset;
exports.xopen = xopen;
exports.xclose = xclose;
exports.xcloseex = xcloseex;
exports.xswitch = xswitch;
exports.xsendex = xsendex;
exports.xrecvex = xrecvex;
const ediabasx_core_1 = require("@emdzej/ediabasx-core");
const register_values_1 = require("./register-values");
function toBytes(registers, ref) {
    return (0, register_values_1.getBinaryValue)(registers, ref);
}
function fromBytes(registers, ref, data) {
    (0, register_values_1.setBinaryValue)(registers, ref, data);
}
function assertConnected(interfaceClass) {
    if (interfaceClass.isConnected && !interfaceClass.isConnected()) {
        throw new ediabasx_core_1.EdiabasError(ediabasx_core_1.EdiabasErrorCodes.UNKNOWN, "Interface is not connected");
    }
}
function isTimeoutError(error) {
    return error instanceof Error && error.name === "EdiabasTimeoutError";
}
function wrapInterfaceError(error, action) {
    if (isTimeoutError(error)) {
        throw new ediabasx_core_1.EdiabasError(ediabasx_core_1.EdiabasErrorCodes.UNKNOWN, `${action} timed out`);
    }
    if (error instanceof Error) {
        throw new ediabasx_core_1.EdiabasError(ediabasx_core_1.EdiabasErrorCodes.UNKNOWN, `${action} failed: ${error.message}`);
    }
    throw new ediabasx_core_1.EdiabasError(ediabasx_core_1.EdiabasErrorCodes.UNKNOWN, `${action} failed`);
}
function vmTraceEnabled() {
    // EDIABASX_VERBOSE=1 already gates ediabas-level logs in the CLI; reuse it
    // for VM-level xsend tracing so a single env var lights everything up.
    return typeof process !== "undefined" && process.env?.EDIABASX_VERBOSE === "1";
}
function toHex(bytes) {
    let out = "";
    for (let i = 0; i < bytes.length; i++) {
        if (i > 0)
            out += " ";
        out += bytes[i].toString(16).padStart(2, "0").toUpperCase();
    }
    return out;
}
function traceXsend(request, data) {
    if (!vmTraceEnabled())
        return;
    process.stderr.write(`[vm] xsend  req(${request.length})=${toHex(request)}  resp(${data.length})=${toHex(data)}\n`);
}
function assertCapability(value, action) {
    if (value === undefined || value === null) {
        // C# `EdInterfaceObd` returns EDIABAS_IFH_0056 ("Function not supported by
        // interface") when an optional capability is missing on the active link.
        throw new ediabasx_core_1.EdiabasError(ediabasx_core_1.EdiabasErrorCodes.EDIABAS_IFH_0056, `${action} is not supported`);
    }
    return value;
}
async function xconnect(interfaceClass) {
    try {
        await interfaceClass.connect();
    }
    catch (error) {
        wrapInterfaceError(error, "Connect");
    }
}
async function xhangup(interfaceClass) {
    try {
        await interfaceClass.disconnect();
    }
    catch (error) {
        wrapInterfaceError(error, "Disconnect");
    }
}
async function sendAndReceive(registers, interfaceClass, response, request, timeoutMs) {
    assertConnected(interfaceClass);
    const requestBytes = toBytes(registers, request);
    const timeout = timeoutMs ? (0, register_values_1.getIntValue)(registers, timeoutMs) : undefined;
    try {
        if (interfaceClass.transmitData) {
            const data = await interfaceClass.transmitData(requestBytes);
            traceXsend(requestBytes, data);
            fromBytes(registers, response, data);
            return;
        }
        await interfaceClass.send(requestBytes);
        const data = await interfaceClass.receive(timeout);
        traceXsend(requestBytes, data);
        fromBytes(registers, response, data);
    }
    catch (error) {
        wrapInterfaceError(error, "Send and receive");
    }
}
async function xsend(registers, interfaceClass, response, request) {
    await sendAndReceive(registers, interfaceClass, response, request);
}
async function xsendRaw(registers, interfaceClass, response, requestBytes) {
    assertConnected(interfaceClass);
    try {
        if (interfaceClass.transmitData) {
            const data = await interfaceClass.transmitData(requestBytes);
            traceXsend(requestBytes, data);
            fromBytes(registers, response, data);
            return;
        }
        await interfaceClass.send(requestBytes);
        const data = await interfaceClass.receive();
        traceXsend(requestBytes, data);
        fromBytes(registers, response, data);
    }
    catch (error) {
        wrapInterfaceError(error, "Send and receive");
    }
}
async function xrecv(registers, interfaceClass, destination, timeoutMs) {
    assertConnected(interfaceClass);
    const timeout = timeoutMs ? (0, register_values_1.getIntValue)(registers, timeoutMs) : undefined;
    try {
        const data = await interfaceClass.receive(timeout);
        fromBytes(registers, destination, data);
    }
    catch (error) {
        wrapInterfaceError(error, "Receive");
    }
}
async function xsendr(registers, interfaceClass, response, request, timeoutMs) {
    await sendAndReceive(registers, interfaceClass, response, request, timeoutMs);
}
async function xreset(interfaceClass) {
    try {
        if (interfaceClass.reset) {
            await interfaceClass.reset();
            return;
        }
        await interfaceClass.disconnect();
        await interfaceClass.connect();
    }
    catch (error) {
        wrapInterfaceError(error, "Reset");
    }
}
function xtype(registers, interfaceClass, destination) {
    assertConnected(interfaceClass);
    // C# `OpXtype` writes `interfaceClass.InterfaceType` directly — no
    // class-name fallback. An interface that doesn't expose the property
    // results in an empty string, never a fabricated value.
    const typeValue = (interfaceClass.getInterfaceType && interfaceClass.getInterfaceType()) ??
        interfaceClass.interfaceType ??
        interfaceClass.type ??
        "";
    (0, register_values_1.setStringValue)(registers, destination, typeValue);
}
function xvers(registers, interfaceClass, destination) {
    assertConnected(interfaceClass);
    const versionValue = (interfaceClass.getInterfaceVersion && interfaceClass.getInterfaceVersion()) ??
        interfaceClass.interfaceVersion ??
        interfaceClass.version ??
        0;
    (0, register_values_1.setIntValue)(registers, destination, versionValue);
}
async function xsetpar(registers, interfaceClass, source) {
    await xsetparBytes(interfaceClass, toBytes(registers, source));
}
/**
 * xsetpar variant taking raw bytes — used by the interpreter dispatch when arg0
 * is a literal/indexed operand rather than a string register.
 *
 * EdiabasLib `OpXsetpar` reads a byte array whose first element selects the
 * stride of the remaining UInt32 values, but in practice C# `EdInterfaceObd`
 * decodes the bytes as `UInt32[]` (4 bytes per value, little-endian) regardless
 * of the per-element stride hint, because `xsetpar` is invoked with a raw
 * buffer of the concept's parameter array. We mirror that by treating the
 * payload as packed little-endian uints.
 */
async function xsetparBytes(interfaceClass, dataArray) {
    assertConnected(interfaceClass);
    // Determine the per-element stride. Common BMW SGBDs encode the parameter
    // array as a sequence of UInt16s (stride=2) and stash the type marker in
    // dataArray[1] = 0x00. UInt32 encoding (stride=4) uses dataArray[1] = 0x01.
    // 1-byte encoding (stride=1) uses dataArray[1] = 0xFF.
    let dataTypeLen = 4;
    if (dataArray.length >= 2) {
        switch (dataArray[1]) {
            case 0x00:
                dataTypeLen = 2;
                break;
            case 0x01:
                dataTypeLen = 4;
                break;
            case 0xff:
                dataTypeLen = 1;
                break;
        }
    }
    let parsArray = [];
    if (dataTypeLen > 0 && dataArray.length % dataTypeLen === 0) {
        const length = dataArray.length / dataTypeLen;
        parsArray = new Array(length);
        for (let i = 0; i < length; i++) {
            const offset = i * dataTypeLen;
            let value = dataArray[offset];
            if (dataTypeLen >= 2) {
                value |= dataArray[offset + 1] << 8;
            }
            if (dataTypeLen >= 4) {
                value |= (dataArray[offset + 2] << 16) | (dataArray[offset + 3] << 24);
            }
            parsArray[i] = value >>> 0;
        }
    }
    try {
        if (interfaceClass.setCommParameter) {
            await interfaceClass.setCommParameter(parsArray);
            return;
        }
        if ("commParameter" in interfaceClass) {
            interfaceClass.commParameter = parsArray;
            return;
        }
        if (interfaceClass.setParameter) {
            for (let i = 0; i < parsArray.length; i++) {
                await interfaceClass.setParameter(i, parsArray[i]);
            }
            return;
        }
        throw new ediabasx_core_1.EdiabasError(ediabasx_core_1.EdiabasErrorCodes.EDIABAS_IFH_0056, "Set parameters is not supported");
    }
    catch (error) {
        wrapInterfaceError(error, "Set parameters");
    }
}
async function xawlen(registers, interfaceClass, source) {
    await xawlenBytes(interfaceClass, toBytes(registers, source));
}
/** xawlen variant taking raw bytes (for literal/indexed operands). */
async function xawlenBytes(interfaceClass, dataArray) {
    assertConnected(interfaceClass);
    if (dataArray.length % 2 !== 0) {
        throw new ediabasx_core_1.EdiabasError(ediabasx_core_1.EdiabasErrorCodes.UNKNOWN, "Invalid answer length data");
    }
    const length = dataArray.length / 2;
    const answerArray = new Array(length);
    for (let i = 0; i < length; i++) {
        const offset = i * 2;
        const value = dataArray[offset] | (dataArray[offset + 1] << 8);
        answerArray[i] = value;
    }
    try {
        if (interfaceClass.setAnswerLengths) {
            await interfaceClass.setAnswerLengths(answerArray);
            return;
        }
        if ("commAnswerLen" in interfaceClass) {
            interfaceClass.commAnswerLen = answerArray;
            return;
        }
        if (interfaceClass.setAnswerLength && answerArray.length > 0) {
            await interfaceClass.setAnswerLength(answerArray[0]);
            return;
        }
        throw new ediabasx_core_1.EdiabasError(ediabasx_core_1.EdiabasErrorCodes.EDIABAS_IFH_0056, "Set answer length is not supported");
    }
    catch (error) {
        wrapInterfaceError(error, "Set answer length");
    }
}
async function xsendf(registers, interfaceClass, payload) {
    assertConnected(interfaceClass);
    try {
        await interfaceClass.transmitFrequent(toBytes(registers, payload));
    }
    catch (error) {
        wrapInterfaceError(error, "Send frequent");
    }
}
async function xrequf(registers, interfaceClass, response) {
    assertConnected(interfaceClass);
    try {
        const data = await interfaceClass.receiveFrequent();
        fromBytes(registers, response, data);
    }
    catch (error) {
        wrapInterfaceError(error, "Receive frequent");
    }
}
async function xstopf(interfaceClass) {
    assertConnected(interfaceClass);
    try {
        await interfaceClass.stopFrequent();
    }
    catch (error) {
        wrapInterfaceError(error, "Stop frequent");
    }
}
async function xkeyb(registers, interfaceClass, destination) {
    try {
        const keyBytes = interfaceClass.keyBytes ??
            (interfaceClass.getKeyBytes ? await interfaceClass.getKeyBytes() : undefined);
        if (keyBytes) {
            fromBytes(registers, destination, keyBytes);
        }
    }
    catch (error) {
        wrapInterfaceError(error, "Key bytes");
    }
}
function xstate(registers, interfaceClass, destination) {
    const stateValue = (interfaceClass.getState && interfaceClass.getState()) ?? interfaceClass.state;
    if (stateValue) {
        fromBytes(registers, destination, stateValue);
    }
}
async function xboot(interfaceClass) {
    const boot = assertCapability(interfaceClass.boot, "Boot");
    try {
        await boot();
    }
    catch (error) {
        wrapInterfaceError(error, "Boot");
    }
}
async function xreps(registers, interfaceClass, count) {
    assertConnected(interfaceClass);
    const value = (0, register_values_1.getIntValue)(registers, count);
    try {
        if (interfaceClass.setRepeatCounter) {
            await interfaceClass.setRepeatCounter(value);
            return;
        }
        if ("commRepeats" in interfaceClass) {
            interfaceClass.commRepeats = value;
            return;
        }
        throw new ediabasx_core_1.EdiabasError(ediabasx_core_1.EdiabasErrorCodes.EDIABAS_IFH_0056, "Set repeat counter is not supported");
    }
    catch (error) {
        wrapInterfaceError(error, "Set repeat counter");
    }
}
async function xgetport(registers, interfaceClass, destination, portIndex) {
    const getPort = assertCapability(interfaceClass.getPort, "Get port");
    try {
        const portValue = await getPort(portIndex & 0xff);
        (0, register_values_1.setIntValue)(registers, destination, portValue);
    }
    catch (error) {
        wrapInterfaceError(error, "Get port");
    }
}
async function xsetport(registers, interfaceClass, portIndexSource, portValue) {
    const setPort = assertCapability(interfaceClass.setPort, "Set port");
    try {
        const portData = toBytes(registers, portIndexSource);
        const portIndex = portData[0] ?? 0;
        await setPort(portIndex, portValue);
    }
    catch (error) {
        wrapInterfaceError(error, "Set port");
    }
}
async function xbatt(registers, interfaceClass, destination) {
    const batteryVoltage = assertCapability(interfaceClass.batteryVoltage, "Battery voltage");
    try {
        const value = await batteryVoltage;
        if (value !== undefined && value !== null && value !== Number.MIN_SAFE_INTEGER) {
            (0, register_values_1.setIntValue)(registers, destination, value);
        }
    }
    catch (error) {
        wrapInterfaceError(error, "Battery voltage");
    }
}
async function xignit(registers, interfaceClass, destination) {
    const ignitionVoltage = assertCapability(interfaceClass.ignitionVoltage, "Ignition voltage");
    try {
        const value = await ignitionVoltage;
        if (value !== undefined && value !== null && value !== Number.MIN_SAFE_INTEGER) {
            (0, register_values_1.setIntValue)(registers, destination, value);
        }
    }
    catch (error) {
        wrapInterfaceError(error, "Ignition voltage");
    }
}
async function xloopt(registers, interfaceClass, destination) {
    const loopTest = assertCapability(interfaceClass.loopTest, "Loop test");
    try {
        const result = await loopTest;
        (0, register_values_1.setIntValue)(registers, destination, result);
    }
    catch (error) {
        wrapInterfaceError(error, "Loop test");
    }
}
async function xprog(registers, interfaceClass, value) {
    const setProgramVoltage = assertCapability(interfaceClass.setProgramVoltage, "Program voltage");
    try {
        await setProgramVoltage(value);
    }
    catch (error) {
        wrapInterfaceError(error, "Program voltage");
    }
}
async function xraw(registers, interfaceClass, response, request) {
    assertConnected(interfaceClass);
    const rawData = assertCapability(interfaceClass.rawData, "Raw data");
    try {
        const data = await rawData(toBytes(registers, request));
        fromBytes(registers, response, data);
    }
    catch (error) {
        wrapInterfaceError(error, "Raw data");
    }
}
async function xsireset(interfaceClass, timeValue) {
    const switchSiRelais = assertCapability(interfaceClass.switchSiRelais, "Service interval reset");
    try {
        await switchSiRelais(timeValue);
    }
    catch (error) {
        wrapInterfaceError(error, "Service interval reset");
    }
}
async function xopen(registers, interfaceClass, mode) {
    const open = assertCapability(interfaceClass.open, "Open interface");
    const modeValue = mode ? (0, register_values_1.getIntValue)(registers, mode) : undefined;
    try {
        await open(modeValue);
    }
    catch (error) {
        wrapInterfaceError(error, "Open interface");
    }
}
async function xclose(interfaceClass) {
    const close = assertCapability(interfaceClass.close, "Close interface");
    try {
        await close();
    }
    catch (error) {
        wrapInterfaceError(error, "Close interface");
    }
}
async function xcloseex(registers, interfaceClass, mode) {
    const closeEx = assertCapability(interfaceClass.closeEx, "Close interface extended");
    const modeValue = mode ? (0, register_values_1.getIntValue)(registers, mode) : undefined;
    try {
        await closeEx(modeValue);
    }
    catch (error) {
        wrapInterfaceError(error, "Close interface extended");
    }
}
async function xswitch(registers, interfaceClass, index) {
    const switchInterface = assertCapability(interfaceClass.switchInterface, "Switch interface");
    try {
        await switchInterface((0, register_values_1.getIntValue)(registers, index));
    }
    catch (error) {
        wrapInterfaceError(error, "Switch interface");
    }
}
async function xsendex(registers, interfaceClass, source) {
    if (interfaceClass.sendExtended) {
        try {
            await interfaceClass.sendExtended(toBytes(registers, source));
            return;
        }
        catch (error) {
            wrapInterfaceError(error, "Extended send");
        }
    }
    assertConnected(interfaceClass);
    try {
        await interfaceClass.send(toBytes(registers, source));
    }
    catch (error) {
        wrapInterfaceError(error, "Send");
    }
}
async function xrecvex(registers, interfaceClass, destination, timeoutMs) {
    const timeout = timeoutMs ? (0, register_values_1.getIntValue)(registers, timeoutMs) : undefined;
    if (interfaceClass.receiveExtended) {
        try {
            const data = await interfaceClass.receiveExtended(timeout);
            fromBytes(registers, destination, data);
            return;
        }
        catch (error) {
            wrapInterfaceError(error, "Extended receive");
        }
    }
    await xrecv(registers, interfaceClass, destination, timeoutMs);
}
//# sourceMappingURL=communication.js.map